import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { WishlistItemDto } from "@gaole/shared";
import { api } from "../lib/api";
import { rm } from "../lib/format";
import { useAuth } from "../stores/auth";
import { PageHeader } from "../components/storefront/PageHeader";
import { EmptyState, PageLoader } from "../components/ui";
import { ProductImage, ProductPrice, productImageSrc } from "../components/ProductCard";
import { ListingTitle } from "../components/ListingTitle";
import { WishlistHeart } from "../components/wishlist/WishlistHeart";
import { Heart } from "lucide-react";

function ShopWishCard({ item }: { item: Extract<WishlistItemDto, { kind: "SHOP" }> }) {
  const inner = (
    <>
      <div className="relative aspect-square overflow-hidden rounded-t-2xl bg-surface-2">
        <ProductImage
          src={productImageSrc({ displayImages: item.displayImages, images: item.images })}
          alt={item.name}
          backgroundColor={item.imageBackgroundColor}
          className="h-full w-full"
        />
        <div className="absolute right-2 top-2 z-10">
          <WishlistHeart kind="SHOP" productId={item.productId} />
        </div>
        {!item.available && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/80">Unavailable</span>
          </div>
        )}
      </div>
      <div className="p-3">
        <ListingTitle className="text-sm font-semibold">{item.name}</ListingTitle>
        <ProductPrice price={item.price} compareAtPrice={item.compareAtPrice} />
      </div>
    </>
  );

  if (!item.available) {
    return <div className="overflow-hidden rounded-2xl border border-white/5 bg-surface opacity-80">{inner}</div>;
  }
  return (
    <Link to={`/shop/${item.slug}`} className="block overflow-hidden rounded-2xl border border-white/5 bg-surface">
      {inner}
    </Link>
  );
}

function MarketWishCard({ item }: { item: Extract<WishlistItemDto, { kind: "MARKETPLACE" }> }) {
  const href = `/market/${item.slug}?shop=${item.seller.shopSlug}`;
  const inner = (
    <>
      <div className="relative aspect-square overflow-hidden rounded-t-2xl bg-surface-2">
        {item.images[0] ? (
          <img src={item.images[0]} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-white/30">No image</div>
        )}
        <div className="absolute right-2 top-2 z-10">
          <WishlistHeart kind="MARKETPLACE" listingId={item.listingId} />
        </div>
        {!item.available && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <span className="text-xs font-semibold uppercase tracking-wide text-white/80">Unavailable</span>
          </div>
        )}
      </div>
      <div className="p-3">
        <ListingTitle className="text-sm font-semibold">{item.title}</ListingTitle>
        <p className="text-[11px] text-white/45">{item.seller.shopName}</p>
        <p className="mt-1 font-display text-sm font-semibold text-brand-pink">{rm(item.price)}</p>
      </div>
    </>
  );

  if (!item.available) {
    return <div className="overflow-hidden rounded-2xl border border-white/5 bg-surface opacity-80">{inner}</div>;
  }
  return (
    <Link to={href} className="block overflow-hidden rounded-2xl border border-white/5 bg-surface">
      {inner}
    </Link>
  );
}

export function Wishlist() {
  const { moduleEnabled } = useAuth();
  const shopOn = moduleEnabled("ecommerce");
  const marketOn = moduleEnabled("marketplace");

  const q = useQuery({
    queryKey: ["wishlist", "list"],
    queryFn: () => api<{ items: WishlistItemDto[]; total: number }>("/wishlist?pageSize=48"),
  });

  if (q.isLoading) return <PageLoader />;

  const items = q.data?.items ?? [];
  const shopItems = items.filter((i) => i.kind === "SHOP");
  const marketItems = items.filter((i) => i.kind === "MARKETPLACE");

  return (
    <div className="space-y-6 px-4 pt-4 lg:px-6">
      <PageHeader title="Wishlist" description="Items you saved from the shop and marketplace." />

      {items.length === 0 ? (
        <EmptyState
          icon={Heart}
          title="Nothing saved yet"
          hint="Tap the heart on a product or listing to save it here."
          action={
            shopOn ? (
              <Link to="/shop" className="text-sm font-semibold text-brand-pink">
                Browse shop
              </Link>
            ) : marketOn ? (
              <Link to="/market" className="text-sm font-semibold text-brand-pink">
                Browse marketplace
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          {shopOn && shopItems.length > 0 ? (
            <section className="space-y-3">
              <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Shop</h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {shopItems.map((item) => (
                  <ShopWishCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ) : null}
          {marketOn && marketItems.length > 0 ? (
            <section className="space-y-3">
              <h2 className="font-display text-xs font-semibold uppercase tracking-widest text-white/40">Marketplace</h2>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-4">
                {marketItems.map((item) => (
                  <MarketWishCard key={item.id} item={item} />
                ))}
              </div>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
